import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { PHOTOS } from "photos";
import Photo from "components/Photo";

const SIZES = "(max-width: 880px) 86vw, 50vw";

/**
 * How long each photograph holds before the gallery moves on by itself.
 *
 * The pace eases off rather than staying constant: the first two moves come
 * quickly enough to show the gallery is alive and worth watching, then it
 * settles so it stops competing with the text beside it. The argument is how
 * many moves have already happened.
 */
const holdFor = (moves) => {
  if (moves < 2) return 3000;
  if (moves < 4) return 4000;
  return 5000;
};
const LAST = PHOTOS.length - 1;

/**
 * One clone at each end turns a finite track into a loop.
 *
 * Scrolling forward past the last photograph lands on a copy of the first;
 * once the scroll settles we jump silently to the real first slide. The copy
 * is pixel-identical and the jump is instant, so the seam is invisible, and
 * the swipe keeps native momentum the whole way.
 */
const SLIDES = [
  { ...PHOTOS[LAST], key: "head-clone", real: LAST },
  ...PHOTOS.map((photo, i) => ({ ...photo, key: photo.slug, real: i })),
  { ...PHOTOS[0], key: "tail-clone", real: 0 },
];

const HEAD_CLONE = 0;
const FIRST_REAL = 1;
const LAST_REAL = PHOTOS.length;
const TAIL_CLONE = PHOTOS.length + 1;

const wantsStillness = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Chevron = ({ direction }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polyline points={direction === "prev" ? "15 5 8 12 15 19" : "9 5 16 12 9 19"} />
  </svg>
);

const Gallery = () => {
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const position = useRef(FIRST_REAL);
  const hop = useRef(null);
  const [index, setIndex] = useState(0);

  // Autoplay runs until you take control, then stops for good. A carousel
  // that starts moving again under your cursor is worse than one that simply
  // hands over. Anyone whose system asks for reduced motion never gets it.
  const [autoplay, setAutoplay] = useState(() => !wantsStillness());
  const stopAutoplay = useCallback(() => setAutoplay(false), []);

  const hovering = useRef(false);
  const moves = useRef(0);

  const centreOf = useCallback((slot) => {
    const track = trackRef.current;
    const slide = slideRefs.current[slot];
    if (!track || !slide) return 0;
    return slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
  }, []);

  const settle = useCallback((slot, behavior) => {
    const track = trackRef.current;
    if (!track) return;
    position.current = slot;
    const real = SLIDES[slot] && SLIDES[slot].real;
    if (real !== undefined) setIndex(real);
    track.scrollTo({ left: centreOf(slot), behavior });
  }, [centreOf]);

  /** Step off a clone onto its real twin, instantly and invisibly. */
  const normalise = useCallback(() => {
    if (position.current === HEAD_CLONE) settle(LAST_REAL, "auto");
    else if (position.current === TAIL_CLONE) settle(FIRST_REAL, "auto");
  }, [settle]);

  const glide = useCallback((slot) => {
    const still = wantsStillness();
    settle(slot, still ? "auto" : "smooth");

    // Landing on a clone has to be undone once the glide finishes. This runs
    // on a timer rather than off a scroll event, because a scroll that does
    // not actually move the track fires no event, which would strand us on
    // the clone with nowhere left to go.
    clearTimeout(hop.current);
    if (slot === HEAD_CLONE || slot === TAIL_CLONE) {
      hop.current = setTimeout(normalise, still ? 0 : 540);
    }
  }, [settle, normalise]);

  /** Move by whole photographs, counting from a real slide rather than a clone. */
  const step = useCallback((delta) => {
    let from = position.current;
    if (from === HEAD_CLONE) from = LAST_REAL;
    else if (from === TAIL_CLONE) from = FIRST_REAL;
    glide(from + delta);
  }, [glide]);

  useEffect(() => () => clearTimeout(hop.current), []);

  // Open on the first real photograph, not on the leading clone.
  useLayoutEffect(() => {
    settle(FIRST_REAL, "auto");
  }, [settle]);

  // A swipe moves the track without going through settle(), so read the
  // position back, and step off either clone the moment the scroll stops.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let timer;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const middle = track.scrollLeft + track.clientWidth / 2;
        let nearest = FIRST_REAL;
        let shortest = Infinity;
        slideRefs.current.forEach((slide, slot) => {
          if (!slide) return;
          const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - middle);
          if (distance < shortest) {
            shortest = distance;
            nearest = slot;
          }
        });

        if (nearest === HEAD_CLONE) settle(LAST_REAL, "auto");
        else if (nearest === TAIL_CLONE) settle(FIRST_REAL, "auto");
        else {
          position.current = nearest;
          setIndex(nearest - 1);
        }
      }, 90);
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      track.removeEventListener("scroll", onScroll);
    };
  }, [settle]);

  // Listen for the input itself rather than trying to work out, after the
  // fact, whether a scroll was ours or yours. A wheel, a finger or a pointer
  // on the gallery is unambiguous; a scroll event is not, because our own
  // movement produces those too.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || !autoplay) return undefined;
    const hands_over = () => stopAutoplay();
    const events = ["wheel", "touchstart", "pointerdown"];
    events.forEach((e) => track.addEventListener(e, hands_over, { passive: true }));
    return () => events.forEach((e) => track.removeEventListener(e, hands_over));
  }, [autoplay, stopAutoplay]);

  // A chain of timeouts rather than an interval, because each wait depends on
  // how many moves have happened.
  useEffect(() => {
    if (!autoplay) return undefined;
    let id;
    const tick = () => {
      // Don't burn through the set in a background tab, and don't slide a
      // photograph away from under someone who is looking at it. Waiting a
      // full beat again means it never lurches the instant you look away.
      if (document.hidden || hovering.current) {
        id = setTimeout(tick, holdFor(moves.current));
        return;
      }
      step(1);
      moves.current += 1;
      id = setTimeout(tick, holdFor(moves.current));
    };
    id = setTimeout(tick, holdFor(moves.current));
    return () => clearTimeout(id);
  }, [autoplay, step]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        stopAutoplay();
        step(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        stopAutoplay();
        step(-1);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [step, stopAutoplay]);

  useEffect(() => {
    const onResize = () => settle(position.current, "auto");
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [settle]);

  return (
    <figure className="viewer">
      <div
        className="gallery"
        onMouseEnter={() => { hovering.current = true; }}
        onMouseLeave={() => { hovering.current = false; }}
      >
        <button
          className="chev prev"
          type="button"
          aria-label="Previous photograph"
          onClick={() => { stopAutoplay(); step(-1); }}
        >
          <Chevron direction="prev" />
        </button>

        <div className="track" ref={trackRef} role="group" aria-label="Photographs">
          {SLIDES.map((slide, slot) => (
            <figure
              key={slide.key}
              ref={(el) => { slideRefs.current[slot] = el; }}
              className={`slide${slide.real === index ? " is-active" : ""}`}
              aria-hidden={slide.key.endsWith("-clone") ? "true" : undefined}
              onClick={() => { stopAutoplay(); return slot === position.current ? step(1) : glide(slot); }}
            >
              <Photo
                slug={slide.slug}
                alt={slide.key.endsWith("-clone") ? "" : slide.alt}
                sizes={SIZES}
                priority={slot === FIRST_REAL}
              />
            </figure>
          ))}
        </div>

        <button
          className="chev next"
          type="button"
          aria-label="Next photograph"
          onClick={() => { stopAutoplay(); step(1); }}
        >
          <Chevron direction="next" />
        </button>
      </div>

      <div className="dots">
        {PHOTOS.map((photo, i) => (
          <button
            key={photo.slug}
            type="button"
            aria-label={photo.caption}
            aria-current={i === index}
            onClick={() => { stopAutoplay(); glide(i + 1); }}
          />
        ))}
      </div>
    </figure>
  );
};

export default Gallery;
